<template>
  <div 
    class="message-wrap" 
    v-memo="[msg, isFirstInGroup, isTooltipActive, isVisible, currentUser, activeEmoji, firstReactionEmoji]"
    :class="{'mini': miniChat, 'author': msg.user_id === myUser.id}"
  >
    <template v-if="!miniChat && !isMobile">
      <span v-if="isFirstInGroup" class="message-wrap__img">
        <ChatAvatar :url="isAuthor ? (myAvatar?.src || '') : (currentUser?.user?.avatar || '')" :name="isAuthor ? (myUser.username || '') : (currentUser?.user?.username || '')" :size="40"/>
      </span>
      <span v-else class="message-wrap__img"></span>
    </template>
    <div class="message-wrap__block" 
        @touchstart.stop="handleTouchStart($event, msg)"
        @touchend="handleTouchEnd"
        @touchcancel="handleTouchEnd"
        @touchmove="handleTouchMove"
        @contextmenu.prevent="handleRightClick()">
      <div class="message-wrap__block-wrap">
        <div class="message-wrap__block-reply" v-if="msg?.reply_to" @click="openRepliedMsg(msg.reply_to.id)">
          <div class="message-wrap__block-reply-user">{{ msg.reply_to.user_id === myUser?.id ? $t("You") : currentUser?.user.username }}</div>
          <div class="message-wrap__block-reply-text" v-html="msg.reply_to?.text ? sanitizeHtml(msg.reply_to.text) : fileOrImage(msg.reply_to.files)"></div>
        </div>
        <div class="message-wrap__block-files" :data-msg-id="msg.id" v-if="msg?.files && msg?.files.length > 0" :class="[fileImageClass, { mini: miniChat || isMobile }]">
          <div v-for="file of sortedFiles" 
            :key="file.id" 
            :class="{
              'message-wrap__block-files-img': file.preview_url, 
              'message-wrap__block-files-file': !file.preview_url,
              skeleton: !isVisible && file.preview_url
            }"
            :style="getFileStyle(file)">
            <img v-if="file.preview_url"
              @click="$emit('open-img', [file.id, false])"
              draggable="false"
              class="lazy-image"
              :src="isVisible ? file.preview_url : null"
              :alt="file.original_name">
            <template v-else>
              <div class="file__preview" :class="getPreviewClass(file.original_name)"></div>
              <span>{{ truncateName(file.original_name) }}</span>
              <div class="file__btn">
                <span v-if="getPreviewClass(file.original_name) !== 'zip'" @click="$emit('open-file', [file.id, file.original_name, 'view'])"><BaseIcon v-once :name="'message-file-view'" size="20"/></span>
                <span @click="$emit('open-file', [file.id, file.original_name, 'download'])"><BaseIcon v-once :name="'message-file-download'" size="20"/></span>
              </div>
            </template>
          </div>
        </div>
        <div class="message-wrap__block-msg" v-if="safeHtml" v-html="safeHtml"></div>
        <div class="message-wrap__block-bottom">
          <div class="message-wrap__block-bottom-reactions">
            <div class="reaction my" v-if="firstReactionEmoji && firstReactionEmoji === activeEmoji" @click="reactionsEmoji(activeEmoji)">
              <span class="reaction__emoji">{{ emojiChar(firstReactionEmoji) }}</span>
              <div class="reaction__imgs">
                <span class="reaction__avatar"><ChatAvatar :url="myAvatar?.src || ''" :name="myUser.username || ''" :size="16"/></span>
                <span class="reaction__avatar"><ChatAvatar :url="currentUser?.user.avatar || ''" :name="currentUser?.user?.username || ''" :size="16"/></span>
              </div>
            </div>
            <template v-else>
              <div class="reaction" v-if="firstReactionEmoji" @click="reactionsEmoji(firstReactionEmoji)">
                <span class="reaction__emoji">{{ emojiChar(firstReactionEmoji) }}</span>
                <span class="reaction__avatar"><ChatAvatar :url="currentUser?.user.avatar || ''" :name="currentUser?.user?.username || ''" :size="16"/></span>
              </div>
              <div class="reaction my" v-if="activeEmoji" @click="reactionsEmoji(activeEmoji)">
                <span class="reaction__emoji">{{ emojiChar(activeEmoji) }}</span>
                <span class="reaction__avatar"><ChatAvatar :url="myAvatar?.src || ''" :name="myUser.username || ''" :size="16"/></span>
              </div>
            </template>
            
          </div>
          <div class="message-wrap__block-wrap-time">
            <span v-if="msg.is_updated">
              {{ $t("edited") }}
            </span>
            {{ timeString }}
            <span v-if="msg.just_sent && msg.user_id === myUser.id">
              <BaseIcon v-once :name="'message-just-send'" size="14"/>                                        
            </span>  
            <span v-else-if="msg.user_id === myUser.id" :class="{ readed: msg.is_read }">
              <BaseIcon v-once :name="'message-is-read'" size="14"/>                   
            </span>
          </div>
        </div>
      </div>
      <transition name="msg-tooltip">
        <div class="message-wrap__block-tooltip" :class="{ mini: miniChat || isMobile }" @click.stop.prevent v-if="isTooltipActive">
          <div class="reactions">
            <div class="reactions__top">
              <div class="emoji" v-for="emoji of reactionsTop" 
                :key="emoji"
                :class="{active: activeEmoji === emoji}"
                @click="reactionsEmoji(emoji)">
                <span class="emoji__char">{{ emojiChar(emoji) }}</span>
              </div>
              <span class="emoji__btn" :class="{active: isActiveEmoji}" @click="isActiveEmoji = !isActiveEmoji">
                <BaseIcon v-once :name="'emoji-arrow'" size="14"/>
              </span>
            </div>
            <div class="reactions__bottom" :class="{active: isActiveEmoji}">
              <div class="emoji" v-for="emoji of reactionsBottom" 
                :key="emoji"
                :class="{active: activeEmoji === emoji}"
                @click="reactionsEmoji(emoji)">
                <span class="emoji__char">{{ emojiChar(emoji) }}</span>
              </div>
            </div>
          </div>
          <div class="message-wrap__block-tooltip-wrap" :class="{active: !isActiveEmoji}">
            <div class="tooltip-el reply" @click.prevent="replyMsg(msg)">
              <BaseIcon v-once :name="'message-reply'" size="20"/>
              <span>{{ $t("Reply") }}</span>
            </div>
            <div class="tooltip-el copy" v-if="safeHtml" @click.prevent="copyMessage(msg.text)">
              <BaseIcon v-once :name="'message-copy'" size="20"/>                   
              <span>{{ $t("Copy") }}</span>
            </div>
            <div class="tooltip-el edit" @click.prevent="updateMsg(msg)" v-if="msg.user_id === myUser.id">
              <BaseIcon v-once :name="'message-update'" size="20"/>
              <span>{{ $t("Edit") }}</span>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>
<script>
  import ChatAvatar from './ChatAvatar.vue';
import BaseIcon from '/webpack-assets/vue/components/BaseIcon.vue';
import { REACTIONS_TOP, REACTIONS_BOTTOM, REACTION_EMOJI } from '/webpack-assets/vue/const/chat/reactions.js';
import { escapeHtml } from '/webpack-assets/vue/utils/escapeHtml.js';
import { sanitizeHtml } from '/webpack-assets/vue/utils/sanitizeHtml.js';
export default {
  components: {
      ChatAvatar,
    BaseIcon
  },
  data() {
    return {
      touchTimeout: null,
      isTouchMoved: false,
      myUser: window?.common.user,
      // same source as the header avatar
      myAvatar: window.common?.user?.avatar || null,
      isActiveEmoji: false,
    };
  },
  props: {
    msg: {
      type: Object,
      required: true
    },
    isTooltipActive: {
      type: Boolean,
      default: false
    },
    currentUserUuidProp: {
      type: String,
      required: true
    },
    supportImg: {
      type: String,
      required: true
    },
    isMobile: {
      type: Boolean,
      default: false
    },
    isTablet: {
      type: Boolean,
      default: false
    },
    miniChat: {
      type: Boolean,
      default: false
    },
    currentUser: {
      type: Object,
      required: false
    },
    isVisible: {
      type: Boolean,
      default: false
    },
    isFirstInGroup: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    isTooltipActive(bool) {
      if(!bool) this.isActiveEmoji = false;
    }
  },
  computed: {
    isAuthor() {
      return this.msg.user_id === this.myUser.id;
    },
    userAvatarUrl() {
      return this.msg.user_id === this.myUser.id 
        ? this.myAvatar?.src 
        : (this.currentUser?.user.avatar || this.supportImg);
    },
    safeHtml() {
      return this.msg.text && this.msg.text.trim() ? sanitizeHtml(this.msg.text) : null;
    },
    reactionsTop() {
      return REACTIONS_TOP;
    },
    reactionsBottom() {
      return REACTIONS_BOTTOM;
    },
    // my reaction comes from msg.reactions, which ChatBody updates optimistically on click
    activeEmoji() {
      return this.msg.reactions?.find(r => r.user_id === this.myUser.id)?.emoji || null;
    },
    firstReactionEmoji() {
      return this.msg.reactions?.find(r => r.user_id !== this.myUser.id)?.emoji || null;
    },
    fileImageClass() {
      const files = this.msg?.files || [];
      const previewFiles = files.filter(f => f.preview_url);

      return {
        ['file-' + previewFiles.length]: previewFiles.length > 0,
        'document': previewFiles.length < files.length,
        'files-wrap': previewFiles.length === 0 && files.length > 0
      };
    },
    timeString() {
      try {
        return new Date(this.msg.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
      } catch (e) {
        return '';
      }
    },
    sortedFiles() {
      if (!this.msg?.files?.length) return [];

      return [...this.msg.files].sort((a, b) => {
        const aHasPreview = !!a.preview_url;
        const bHasPreview = !!b.preview_url;

        if (aHasPreview !== bHasPreview) {
          return bHasPreview - aHasPreview;
        }

        return a.timemark - b.timemark;
      });
    }
  },
  methods: {
    emojiChar(name) {
      return REACTION_EMOJI[name] || '';
    },
    escapeHtml,
    sanitizeHtml,
    onImgError(event) {
      event.target.src = this.supportImg;
    },
    getFileStyle(file) {
      if (!file.preview_url) return {};
      const files = this.msg?.files || []; 
      const previewFiles = files.filter(f => f.preview_url);

      // grid tiles take their proportions from the layout, not from the preview
      if (previewFiles.length > 1) {
        return this.isVisible ? { backgroundImage: `url(${file.preview_url})` } : {};
      } else if (file?.preview_width && file?.preview_height) {
        return {
          aspectRatio: `${file.preview_width} / ${file.preview_height}`
        };
      } else if (this.isVisible) {
        return {
          aspectRatio: '1 / 1',
          backgroundImage: `url(${file.preview_url})`
        };
      }
    },
    fileOrImage(files) {
      if (!files || !files.length) return 'unknown';

      const lastFile = files[files.length - 1];
      const name = lastFile.original_name || '';
      const ext = (name.split('.').pop() || '').toLowerCase();
      const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];

      return imageExts.includes(ext) ? this.$t("Image") : this.$t("Document");
    },
    // handleMouseEnter() {
    //   if (!this.isTablet) {
    //     this.$emit('toggle-tooltip', this.msg.id);
    //   }
    // },
    handleRightClick() {
      if (this.isTooltipActive) return;
      this.$emit('toggle-tooltip', this.msg.id);
    },
    getPreviewClass(name) {
      const ext = (this.getExtension(name) || '').toLowerCase();
      const allowed = ['jpg','jpeg','png','gif','pdf','rar','zip','7z','txt','csv','xls','xlsx'];
      return allowed.includes(ext) ? ext.replace('jpeg','jpg') : 'unknown';
    },
    getExtension(name) {
      return name.split('.').pop().toUpperCase();
    },
    truncateName(name) {
      let len = this.miniChat || this.isMobile ? 20 : 25;
      if (name.length <= len) return name;
      
      const extIndex = name.lastIndexOf('.');
      if (extIndex === -1) return name.slice(0, len - 3) + '...';

      const ext = name.slice(extIndex);
      const baseLen = len - ext.length - 3;

      return name.slice(0, baseLen) + '...' + ext;
    },
    handleTouchStart(event, msg) {
      this.isTouchMoved = false;
      if (this.touchTimeout) clearTimeout(this.touchTimeout);
      this.touchTimeout = setTimeout(() => {
        if (!this.isTouchMoved) {
          this.$emit('toggle-tooltip', msg?.id);
        }
      }, 600);
    },
    handleTouchEnd() {
      if (this.touchTimeout) clearTimeout(this.touchTimeout);
    },
    handleTouchMove() {
      this.isTouchMoved = true;
      if (this.touchTimeout) clearTimeout(this.touchTimeout);
    },
    replyMsg(msg) {
      this.$emit('reply-message', msg);
      this.$emit('toggle-tooltip', null);
    },
    updateMsg(msg) {
      this.$emit('update-message', msg);
      this.$emit('toggle-tooltip', null);
    },
    openRepliedMsg(id) {
      this.$emit('open-replied-message', id);
    },
    copyMessage(text) {
        this.$emit('toggle-tooltip', null);
        this.$emit('copy-message', text);
    },
    reactionsEmoji(emoji) {
      const removing = this.activeEmoji === emoji;
      this.$emit('toggle-react', { messageId: this.msg.id, emoji, removing });
      this.$emit('toggle-tooltip', null);
    }
  },
};
</script>